<template>

  
  <FileReport />

  <div class="tutor-page-wrapper">
    <!-- Header - Use TutorHeader for tutors -->
    <TutorHeader />

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-6 sm:pb-8 pt-32 sm:pt-36 md:pt-44">
      <!-- Page Title -->
      <div class="mb-6 sm:mb-8 text-center px-2 sm:px-0">
        <h1 class="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900">My Tutoring Sessions</h1>
        <p class="text-sm sm:text-base md:text-lg text-black mt-2">
          Manage your upcoming sessions, view history, and update your availability.
        </p>
      </div>

      <!-- Summary Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        <SummaryCard
          icon="calendar"
          title="Upcoming Sessions"
          :value="totalUpcomingSessions.toString()"
          :isLoading="isLoadingSessions"
          color="blue"
        />
        <SummaryCard
          icon="check"
          title="Completed Sessions"
          :value="totalCompletedSessions.toString()"
          :isLoading="isLoadingSessions"
          color="green"
        />
        <SummaryCard
          icon="star"
          title="Average Rating"
          :value="averageRating"
          :isLoading="isLoadingSessions"
          color="yellow"
          :isRating="true"
        />
        <SummaryCard
          icon="clock"
          title="Total Hours"
          :value="totalHours"
          :isLoading="isLoadingSessions"
          color="purple"
        />
      </div>

      <!-- Navigation Tabs -->
      <TutorSessionNav :active-tab="activeTab" :tabs="tabs" @tab-change="activeTab = $event" />

      <!-- Tab Content -->
      <div v-if="activeTab === 'upcoming-sessions'" class="space-y-4 sm:space-y-6">
        <!-- Upcoming Sessions Section -->
        <div
          v-if="filteredConfirmedSessions.length > 0 || filteredPendingSessions.length > 0"
          class="bg-white rounded-lg sm:rounded-xl shadow-[3px_3px_0_0_rgba(0,0,0,1)] sm:shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-6 sm:mb-8 overflow-hidden"
        >
          <div class="bg-blue-50 p-3 sm:p-4 md:p-6 rounded-t-lg">
            <div class="flex items-center mb-1 min-w-0">
              <div class="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-start flex-shrink-0">
                <CalendarIcon class="w-5 h-5 sm:w-6 sm:h-6 text-blue-800" />
              </div>
              <h2 class="text-lg sm:text-xl md:text-2xl font-semibold text-blue-900 truncate">Upcoming Sessions</h2>
            </div>
            <p class="text-xs sm:text-sm md:text-base text-blue-800 font-semibold">Your scheduled tutoring sessions.</p>
          </div>

          <div class="p-3 sm:p-4 md:p-6">
            <!-- Search and Filter Section -->
            <SessionSearchAndFilter
              v-model:searchQuery="searchQuery"
              v-model:statusFilter="statusFilter"
              v-model:subjectFilter="subjectFilter"
              filter-context="active"
            />

            <!-- Confirmed Sessions -->
            <TutorUpcomingSessions 
              v-if="filteredConfirmedSessions.length > 0"
              :sessions="filteredConfirmedSessions" 
            />

            <!-- Pending Sessions -->
            <PendingSessions
              v-if="filteredPendingSessions.length > 0"
              :sessions="filteredPendingSessions"
              @confirm="handleConfirmSession"
              @decline="handleDeclineSession"
            />
          </div>
        </div>

        <!-- No Upcoming Sessions State -->
        <div
          v-else
          class="bg-white rounded-lg sm:rounded-xl shadow-[3px_3px_0_0_rgba(0,0,0,1)] sm:shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-6 sm:mb-8 overflow-hidden"
        >
          <div class="bg-blue-50 p-3 sm:p-4 md:p-6 rounded-t-lg">
            <div class="flex items-center mb-1 min-w-0">
              <div class="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-start flex-shrink-0">
                <CalendarIcon class="w-5 h-5 sm:w-6 sm:h-6 text-blue-800" />
              </div>
              <h2 class="text-lg sm:text-xl md:text-2xl font-semibold text-blue-900 truncate">Upcoming Sessions</h2>
            </div>
            <p class="text-xs sm:text-sm md:text-base text-blue-800 font-semibold">Your scheduled tutoring sessions.</p>
          </div>

          <div class="p-3 sm:p-4 md:p-6">
            <!-- Search and Filter Section - Always visible -->
            <SessionSearchAndFilter
              v-model:searchQuery="searchQuery"
              v-model:statusFilter="statusFilter"
              v-model:subjectFilter="subjectFilter"
              filter-context="active"
            />
            
            <div class="text-center py-12 mt-6">
              <CalendarIcon class="mx-auto h-12 w-12 text-gray-400" />
              <h3 class="mt-2 text-sm font-medium text-gray-900">No upcoming sessions</h3>
              <p class="mt-1 text-sm text-gray-500">
                <span v-if="searchQuery || statusFilter !== 'all' || subjectFilter !== 'all'">
                  No sessions match your search. Try adjusting your filters.
                </span>
                <span v-else>
                  You don't have any confirmed or pending sessions scheduled.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Session History Tab -->
      <div v-else-if="activeTab === 'session-history'" class="space-y-4 sm:space-y-6">
      
        <TutorSessionHistory
          :completed-sessions="filteredCompletedSessions"
          :total-completed-sessions="filteredCompletedSessions.length"
          :cancelled-sessions="filteredCancelledSessions"
          :total-cancelled-sessions="filteredCancelledSessions.length"
        />
      </div>

      <div v-else-if="activeTab === 'manage-availability'" class="space-y-4 sm:space-y-6">
        <ManageAvailability />
      </div>
    </div>
  </div>

  <!-- Success/Error Notification -->
  <SessionActionNotification
    :show="showNotification"
    :type="notificationType"
    :title="notificationTitle"
    :message="notificationMessage"
    @close="closeNotification"
  />

  <!-- Demo: Google Calendar Modal (Remove this in production) -->
  <!-- <GoogleCalendarModal /> -->

  <!-- Session Toast Container -->
  <SessionToast />
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import TutorHeader from "@/components/TutorHeader.vue";
import SummaryCard from "@/components/Student-Tutor/SummaryCard.vue";
import TabNavigation from "@/components/Student/StudentSessionNav.vue";
import SessionManager from "@/components/Student-Tutor/SessionManager.vue";
import SessionSearchAndFilter from "@/components/Student-Tutor/SessionSearchAndFilter.vue";
import ManageAvailability from "@/components/Tutor/ManageAvailability.vue";
import TutorUpcomingSessions from "@/components/Tutor/TutorUpcomingSessions.vue";
import PendingSessions from "@/components/Student-Tutor/PendingSessions.vue";
import TutorSessionHistory from "@/components/Tutor/TutorSessionHistory.vue";
import TutorSessionNav from "@/components/Tutor/TutorSessionNav.vue";
import SessionActionNotification from "@/components/Student-Tutor/SessionActionNotification.vue";
import { CalendarIcon } from "@heroicons/vue/24/outline";
import { useTutorSessionStore } from "@/composables/useTutorSessionStore";
import FileReport from "@/components/FileReport.vue";
import SessionToast from "@/components/Student-Tutor/SessionToast.vue";
import type { PendingSession, CompletedSession } from "@/types/session";
import { logDebug, logError } from "@/utils/secureLogger";
// import GoogleCalendarModal from "@/components/GoogleCalendarModal.vue";

const activeTab = ref("upcoming-sessions");

const tabs = [
  { id: "upcoming-sessions", name: "Upcoming Sessions" },
  { id: "session-history", name: "Session History" },
  { id: "manage-availability", name: "Manage Availability" },
];

// Search and filter state
const searchQuery = ref("");
const statusFilter = ref("all");
const subjectFilter = ref("all");

// Filter confirmed sessions based on search and filters
const filteredConfirmedSessions = computed(() => {
  let filtered = confirmedSessions.value || [];

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(
      (session: any) =>
        session.studentName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        session.subject.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  // Apply status filter
  if (statusFilter.value !== "all") {
    filtered = filtered.filter((session: any) => session.status === statusFilter.value);
  }

  // Apply subject filter
  if (subjectFilter.value !== "all") {
    filtered = filtered.filter((session: any) =>
      session.subject.toLowerCase().includes(subjectFilter.value.toLowerCase())
    );
  }

  return filtered;
});

// Filter pending sessions based on search and filters
const filteredPendingSessions = computed(() => {
  let filtered = typedPendingSessions.value || [];

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(
      (session: any) =>
        session.studentName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        session.subject.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  // Apply status filter (pending sessions are always pending, but we can still filter by subject)
  if (statusFilter.value !== "all" && statusFilter.value !== "pending") {
    filtered = [];
  }

  // Apply subject filter
  if (subjectFilter.value !== "all") {
    filtered = filtered.filter((session: any) =>
      session.subject.toLowerCase().includes(subjectFilter.value.toLowerCase())
    );
  }

  return filtered;
});

// Filter completed sessions based on search and filters
const filteredCompletedSessions = computed(() => {
  let filtered = typedCompletedSessions.value || [];

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(
      (session: any) =>
        session.studentName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        session.subject.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  // Apply status filter
  if (statusFilter.value !== "all") {
    filtered = filtered.filter((session: any) => session.status === statusFilter.value);
  }

  // Apply subject filter
  if (subjectFilter.value !== "all") {
    filtered = filtered.filter((session: any) =>
      session.subject.toLowerCase().includes(subjectFilter.value.toLowerCase())
    );
  }

  return filtered;
});

// Filter cancelled sessions based on search and filters
const filteredCancelledSessions = computed(() => {
  let filtered = typedCancelledSessions.value || [];

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(
      (session: any) =>
        session.studentName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        session.subject.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  // Apply subject filter
  if (subjectFilter.value !== "all") {
    filtered = filtered.filter((session: any) =>
      session.subject.toLowerCase().includes(subjectFilter.value.toLowerCase())
    );
  }

  return filtered;
});

// Notification state
const showNotification = ref(false);
const notificationType = ref<"success" | "error">("success");
const notificationTitle = ref("");
const notificationMessage = ref("");

// Use shared tutor session store
const tutorSessionStore = useTutorSessionStore();
const {
  pendingSessions,
  confirmedSessions,
  completedSessions,
  cancelledSessions,
  isLoadingSessions,
  totalCompletedSessions,
  totalCancelledSessions,
  totalUpcomingSessions,
  averageRating,
  totalHours,
  updateSessionStatus,
} = tutorSessionStore;

// Define interfaces that match component expectations
interface StudentSession {
  id: string;
  studentName: string;
  tutorName: string;
  status: "pending" | "confirmed" | "completed" | "cancelled";
  type: "face-to-face" | "online";
  date: string;
  time: string;
  subject: string;
  notes: string;
  location?: string;
}

// Type-safe computed properties
const typedCompletedSessions = computed<CompletedSession[]>(() => {
  return (completedSessions.value || []).map((session: any) => ({
    id: session.id,
    studentName: session.studentName,
    studentProfileImage: session.studentProfileImage,
    tutorName: session.tutorName || "Unknown Tutor", // Add tutorName
    status: "completed" as const,
    type: session.type,
    mode: session.type, // Add mode property (same as type)
    subject: session.subject,
    date: session.date,
    time: session.time,
    location: session.location,
    yourRating: session.yourRating || "0",
    studentRating: session.studentRating || "0",
    tutorRating: session.tutorRating || "0", // Add tutorRating
    feedback: session.feedback || "",
    feedbackStatus: session.feedbackStatus || "pending",
    feedbacks: session.feedbacks || [], // Include feedbacks array
    tutorId: session.tutorId, // Include for feedback label determination
    studentId: session.studentId, // Include for feedback label determination
  }));
});

// Type-safe computed property for cancelled sessions
const typedCancelledSessions = computed(() => {
  return (cancelledSessions.value || []).map((session: any) => ({
    id: session.id,
    studentName: session.studentName,
    studentProfileImage: session.studentProfileImage,
    type: session.type,
    status: session.status, // Add the missing status field
    subject: session.subject,
    date: session.date,
    time: session.time,
    location: session.location,
    cancellationReason: session.cancellationReason || "No reason provided",
    cancelledBy: session.cancelledBy || "system",
    cancelledAt: session.cancelledAt || session.date,
  }));
});

const typedPendingSessions = computed<PendingSession[]>(() => {
  return (pendingSessions.value || [])
    .filter((session: any) => session.status === "pending")
    .map((session: any) => ({
      id: session.id,
      studentName: session.studentName,
      studentProfileImage: session.studentProfileImage,
      status: "pending" as const,
      type: session.type,
      date: session.date,
      time: session.time,
      subject: session.subject,
      notes: session.notes || "",
      location: session.location,
      materials: session.materials || [],
      studentId: session.studentId,
      tutorId: session.tutorId,
      startAt: session.startAt,
      meetingLink: session.meetingLink,
    }));
});

// Force refresh sessions when component is mounted
import { onMounted } from "vue";
onMounted(() => {
  logDebug("TutorSessionView mounted, initializing listener");
  // Re-initialize the listener to ensure we have the latest data
  tutorSessionStore.initializeListener();
});

// Notification methods
const closeNotification = () => {
  showNotification.value = false;
};

// Event handlers for pending sessions
const handleConfirmSession = async (session: PendingSession) => {
  try {
    // Show success notification immediately for better UX
    notificationType.value = "success";
    notificationTitle.value = "Session Approved!";
    notificationMessage.value = `Session with ${session.studentName} has been approved. The student will be notified.`;
    showNotification.value = true;

    // Update session status in Firestore (continues in background)
    const success = await updateSessionStatus(session.id, "confirmed");

    if (success) {
      // Create audit log for session confirmation
      try {
        const { createAuditLog } = await import("@/services/auditLogService");
        const { auth, db } = await import("@/firebase");
        const { doc, getDoc } = await import("firebase/firestore");
        const { getCurrentUserFullName } = await import("@/utils/userUtils");

        const performedByName = await getCurrentUserFullName();
        
        // Get student email from Firestore
        let affectedUserEmail = "";
        if (session.studentId) {
          try {
            const studentDoc = await getDoc(doc(db, "users", session.studentId));
            if (studentDoc.exists()) {
              affectedUserEmail = studentDoc.data().email || "";
            }
          } catch (e) {
            logError("Failed to fetch student email", e);
          }
        }

        await createAuditLog({
          action: "tutor_session_action",
          type: "system_event",
          details: `Tutor confirmed session with ${session.studentName} for ${session.subject} on ${session.date} at ${session.time}`,
          performedById: auth.currentUser?.uid,
          performedByName: performedByName,
          performedByEmail: auth.currentUser?.email || "",
          affectedUserId: session.studentId,
          affectedUserName: session.studentName,
          affectedUserEmail: affectedUserEmail,
          metadata: {
            sessionId: session.id,
            sessionSubject: session.subject,
            sessionDate: session.date,
            sessionTime: session.time,
            sessionMode: session.type,
            actionType: "session_confirmation",
          },
        });
      } catch (auditError) {
        logError("Failed to create audit log for session confirmation", auditError);
      }
    } else {
      logError("Failed to update session status");
      // Show error notification if it actually fails
      notificationType.value = "error";
      notificationTitle.value = "Approval Failed";
      notificationMessage.value = "Failed to approve session. Please try again.";
      showNotification.value = true;
    }
  } catch (error) {
    logError("Error confirming session", error);

    // Show error notification
    notificationType.value = "error";
    notificationTitle.value = "Approval Failed";
    notificationMessage.value =
      error instanceof Error ? error.message : "Failed to approve session. Please try again.";
    showNotification.value = true;
  }
};

const handleDeclineSession = async (session: PendingSession) => {
  try {
    // Show success notification immediately for better UX
    notificationType.value = "success";
    notificationTitle.value = "Session Declined";
    notificationMessage.value = `Session with ${session.studentName} has been declined. The student will be notified.`;
    showNotification.value = true;

    // Update session status in Firestore with decline reason (continues in background)
    const success = await updateSessionStatus(
      session.id,
      "declined",
      "tutor-declined",
      "Session declined by tutor"
    );

    if (success) {
      // Create audit log for session decline
      try {
        const { createAuditLog } = await import("@/services/auditLogService");
        const { auth, db } = await import("@/firebase");
        const { doc, getDoc } = await import("firebase/firestore");
        const { getCurrentUserFullName } = await import("@/utils/userUtils");

        const performedByName = await getCurrentUserFullName();
        
        // Get student email from Firestore
        let affectedUserEmail = "";
        if (session.studentId) {
          try {
            const studentDoc = await getDoc(doc(db, "users", session.studentId));
            if (studentDoc.exists()) {
              affectedUserEmail = studentDoc.data().email || "";
            }
          } catch (e) {
            logError("Failed to fetch student email", e);
          }
        }

        await createAuditLog({
          action: "tutor_session_action",
          type: "system_event",
          details: `Tutor declined session with ${session.studentName} for ${session.subject} on ${session.date} at ${session.time}`,
          performedById: auth.currentUser?.uid,
          performedByName: performedByName,
          performedByEmail: auth.currentUser?.email || "",
          affectedUserId: session.studentId,
          affectedUserName: session.studentName,
          affectedUserEmail: affectedUserEmail,
          metadata: {
            sessionId: session.id,
            sessionSubject: session.subject,
            sessionDate: session.date,
            sessionTime: session.time,
            sessionMode: session.type,
            actionType: "session_decline",
            cancellationReason: "tutor-declined",
          },
        });
      } catch (auditError) {
        logError("Failed to create audit log for session decline", auditError);
      }
    } else {
      logError("Failed to update session status");
      // Show error notification if it actually fails
      notificationType.value = "error";
      notificationTitle.value = "Decline Failed";
      notificationMessage.value = "Failed to decline session. Please try again.";
      showNotification.value = true;
    }
  } catch (error) {
    logError("Error declining session", error);

    // Show error notification
    notificationType.value = "error";
    notificationTitle.value = "Decline Failed";
    notificationMessage.value =
      error instanceof Error ? error.message : "Failed to decline session. Please try again.";
    showNotification.value = true;
  }
};
</script>

<style scoped>
.tutor-page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url("/tutor-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
}

@media (max-width: 768px) {
  .tutor-page-wrapper {
    background-size: cover;
    background-position: center center;
  }
}
</style>
